// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { PurchaseOrderSearch } from '@/purchase-order/components/purchase-order-search';
import {
  Control,
  FieldValues,
  UseControllerProps,
  useController,
} from 'react-hook-form';

/**
 * Props for the FormPurchaseOrderSearch component.
 * Extends react-hook-form's controller props for form integration.
 */
interface Props<Values extends FieldValues> extends UseControllerProps<Values> {
  /**
   * The label text displayed above the input element.
   * Should clearly indicate what type of purchase order to select.
   */
  label: string;

  /**
   * The react-hook-form control instance.
   * Used to integrate the component with the form's state management.
   */
  control: Control<Values>;

  /**
   * If true, the input field is disabled and cannot be interacted with.
   */
  disabled?: boolean;
}

/**
 * A controlled purchase order search component using react-hook-form.
 *
 * Features:
 * - Full form state integration
 * - Validation state handling
 * - Error message display
 * - Controlled value management
 *
 * Form Integration:
 * - Uses react-hook-form's useController for state management
 * - Handles undefined values appropriately
 * - Preserves form validation states
 * - Supports field-level validation
 *
 * The component wraps PurchaseOrderSearch to provide seamless integration
 * with react-hook-form while maintaining all purchase order search functionality.
 */
export function FormPurchaseOrderSearch<Values extends FieldValues>(
  props: Props<Values>
) {
  const { field, fieldState } = useController({
    name: props.name,
    control: props.control,
  });

  // Ensure consistent null value for the controlled input
  // when the field value is undefined
  const value = field.value ?? null;

  return (
    <PurchaseOrderSearch
      ref={field.ref}
      name={field.name}
      label={props.label}
      value={value}
      error={Boolean(fieldState.error)}
      onChange={field.onChange}
      disabled={props.disabled}
      helperText={fieldState.error?.message}
    />
  );
}
